/**
 * Studio playback clock.
 *
 * Mirrors `demo/masters-planner/hooks/usePlaybackLoop.ts`: the clock advances on
 * a private ref every animation frame and poses are applied imperatively, so
 * motion runs at the display refresh rate WITHOUT a React re-render per frame.
 * The store's `currentMs` — which the timeline subscribes to — is written only
 * every `STORE_WRITE_MS`.
 *
 * While paused, an effect applies the current clock on every change, which is
 * what makes scrubbing work.
 */
import { useEffect, useRef } from 'react'
import { useStudioStore } from '../store/studioStore'
import type { ApplyAtFn } from './useStudioActorMotion'

const STORE_WRITE_MS = 100

/** Guard against a huge dt after a background-tab stall. */
const MAX_FRAME_MS = 100

export function useStudioPlayback(
  applyAt: ApplyAtFn,
  durationMs: number,
  loopScene: boolean,
  /**
   * The actor list. Not read here — it's a change signal: while paused, an edit
   * (drag, rotate, path change) must re-apply the pose, or the 3D scene silently
   * lags the store until the next scrub.
   */
  actors: unknown
) {
  const playback = useStudioStore((s) => s.playback)
  const currentMs = useStudioStore((s) => s.currentMs)

  const rafRef = useRef<number | null>(null)
  const liveRef = useRef(0)
  const lastFrameRef = useRef(0)
  const lastWriteRef = useRef(0)
  const applyRef = useRef(applyAt)
  applyRef.current = applyAt

  // ── Playing: imperative 60Hz ──────────────────────────────────────────────
  useEffect(() => {
    if (playback !== 'playing') return

    liveRef.current = useStudioStore.getState().currentMs
    lastFrameRef.current = performance.now()
    lastWriteRef.current = lastFrameRef.current

    const loop = (now: number) => {
      const dt = Math.min(now - lastFrameRef.current, MAX_FRAME_MS)
      lastFrameRef.current = now

      const { speed } = useStudioStore.getState()
      let next = liveRef.current + dt * speed

      if (next >= durationMs) {
        if (loopScene && durationMs > 0) {
          next %= durationMs
        } else {
          liveRef.current = durationMs
          applyRef.current(durationMs)
          useStudioStore.setState({ currentMs: durationMs, playback: 'paused' })
          rafRef.current = null
          return
        }
      }
      liveRef.current = next

      applyRef.current(next)

      if (now - lastWriteRef.current >= STORE_WRITE_MS) {
        lastWriteRef.current = now
        useStudioStore.setState({ currentMs: next })
      }
      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      // Flush the live clock so pausing mid-frame doesn't snap back to the last
      // throttled write — at high speed that gap is large.
      const state = useStudioStore.getState()
      if (state.currentMs !== liveRef.current) {
        useStudioStore.setState({ currentMs: Math.min(liveRef.current, durationMs) })
      }
    }
  }, [playback, durationMs, loopScene])

  // ── Paused / scrubbing / editing: apply on every clock or actor change ────
  useEffect(() => {
    if (playback === 'playing') return
    applyRef.current(currentMs)
  }, [playback, currentMs, actors])

  // Background tabs stall rAF; resetting the frame stamp on return stops a
  // multi-second dt from jumping the scene forward.
  useEffect(() => {
    if (playback !== 'playing') return
    const onVisible = () => {
      if (!document.hidden) {
        lastFrameRef.current = performance.now()
        lastWriteRef.current = lastFrameRef.current
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [playback])
}
